import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import ProviderSidebar from '../../components/ui/ProviderSidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import PatientHeader from './components/PatientHeader';
import SymptomTrendChart from './components/SymptomTrendChart';
import DataTable from './components/DataTable';
import AIAnalysisPanel from './components/AIAnalysisPanel';

const DetailedPatientView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [chartDateRange, setChartDateRange] = useState('30d');
  const [isLoading, setIsLoading] = useState(true);

  // Mock patient data
  const mockPatients = [
    {
      id: 1,
      name: 'Sarah Johnson',
      age: 45,
      gender: 'Female',
      status: 'active',
      riskLevel: 'medium',
      lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      totalSymptomLogs: 47,
      cognitiveScores: 23,
      avgSeverity: 5.2,
      daysSinceLastLog: 1
    },
    {
      id: 2,
      name: 'Michael Chen',
      age: 52,
      gender: 'Male',
      status: 'active',
      riskLevel: 'high',
      lastActivity: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
      totalSymptomLogs: 63,
      cognitiveScores: 31,
      avgSeverity: 7.1,
      daysSinceLastLog: 0
    },
    {
      id: 3,
      name: 'Emma Rodriguez',
      age: 38,
      gender: 'Female',
      status: 'inactive',
      riskLevel: 'low',
      lastActivity: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      totalSymptomLogs: 29,
      cognitiveScores: 18,
      avgSeverity: 3.8,
      daysSinceLastLog: 5
    }
  ];

  // Mock symptom trend data
  const generateSymptomTrendData = () => {
    const data = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      data?.push({
        date: date?.toISOString()?.split('T')?.[0],
        severity: Math.floor(Math.random() * 8) + 1 + (i < 10 ? Math.random() * 2 : 0) // Higher severity in recent days
      });
    }
    return data;
  };

  // Mock symptom logs
  const mockSymptomLogs = [
    {
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      symptomType: 'Headache',
      severity: 7,
      notes: 'Sharp pain in temporal region, lasted 3 hours'
    },
    {
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      symptomType: 'Cognitive Fog',
      severity: 5,
      notes: 'Difficulty concentrating during work tasks'
    },
    {
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      symptomType: 'Fatigue',
      severity: 6,
      notes: 'Persistent tiredness despite adequate sleep'
    },
    {
      date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      symptomType: 'Memory Issues',
      severity: 4,
      notes: 'Forgot appointment, had to check calendar multiple times'
    },
    {
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      symptomType: 'Headache',
      severity: 8,
      notes: 'Severe migraine with light sensitivity'
    },
    {
      date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      symptomType: 'Dizziness',
      severity: 3,
      notes: 'Mild vertigo when standing up quickly'
    },
    {
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      symptomType: 'Cognitive Fog',
      severity: 6,
      notes: 'Word-finding difficulties during conversation'
    },
    {
      date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
      symptomType: 'Fatigue',
      severity: 5,
      notes: 'Energy levels low throughout the day'
    }
  ];

  // Mock cognitive scores
  const mockCognitiveScores = [
    {
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      gameType: 'Reaction Time',
      score: 78,
      reactionTime: 425,
      duration: '5 min'
    },
    {
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      gameType: 'Memory Match',
      score: 85,
      reactionTime: 380,
      duration: '8 min'
    },
    {
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      gameType: 'Pattern Recognition',
      score: 72,
      reactionTime: 520,
      duration: '6 min'
    },
    {
      date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      gameType: 'Reaction Time',
      score: 81,
      reactionTime: 395,
      duration: '5 min'
    },
    {
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      gameType: 'Word Association',
      score: 69,
      reactionTime: 610,
      duration: '7 min'
    },
    {
      date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      gameType: 'Memory Match',
      score: 88,
      reactionTime: 365,
      duration: '8 min'
    }
  ];

  const [symptomTrendData] = useState(generateSymptomTrendData());

  useEffect(() => {
    // Get patient ID from location state or URL params
    const patientId = location?.state?.patientId || 1;
    const patient = mockPatients?.find(p => p?.id === patientId) || mockPatients?.[0];
    setSelectedPatient(patient);
    
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [location?.state]);

  const handleLogout = () => {
    navigate('/login-screen');
  };

  const handleProfileClick = () => {
    console.log('Profile clicked');
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    navigate('/detailed-patient-view', { state: { patientId: patient?.id } });
  };

  const handleDateRangeChange = (range) => {
    setChartDateRange(range);
    // In a real app, this would filter the chart data
    console.log('Date range changed to:', range);
  };

  const handleAIAnalysisGenerated = (analysis) => {
    console.log('AI Analysis generated:', analysis);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/provider-dashboard', clickable: true },
    { label: 'Patients', path: '/provider-dashboard', clickable: true },
    { label: selectedPatient?.name || 'Patient Details', path: null, clickable: false }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader
          userRole="provider"
          userName="Dr. Smith"
          onLogout={handleLogout}
          onProfileClick={handleProfileClick}
        />
        <div className="flex">
          <ProviderSidebar
            patients={mockPatients}
            selectedPatientId={selectedPatient?.id}
            onPatientSelect={handlePatientSelect}
          />
          <main className="flex-1 lg:ml-80 pt-16">
            <div className="p-6">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                <div className="h-32 bg-gray-200 rounded mb-6"></div>
                <div className="h-64 bg-gray-200 rounded mb-6"></div>
                <div className="h-96 bg-gray-200 rounded"></div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!selectedPatient) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader
          userRole="provider"
          userName="Dr. Smith"
          onLogout={handleLogout}
          onProfileClick={handleProfileClick}
        />
        <div className="flex">
          <ProviderSidebar
            patients={mockPatients}
            onPatientSelect={handlePatientSelect}
          />
          <main className="flex-1 lg:ml-80 pt-16">
            <div className="p-6">
              <div className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Patient Not Found</h2>
                <p className="text-gray-600">Please select a patient from the sidebar to view their details.</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <RoleBasedHeader
        userRole="provider"
        userName="Dr. Smith"
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
      />
      <div className="flex">
        <ProviderSidebar
          patients={mockPatients}
          selectedPatientId={selectedPatient?.id}
          onPatientSelect={handlePatientSelect}
        />
        
        <main className="flex-1 lg:ml-80 pt-16">
          <div className="p-6 max-w-7xl mx-auto">
            {/* Breadcrumb Navigation */}
            <BreadcrumbTrail items={breadcrumbItems} className="mb-6" />
            
            {/* Patient Header */}
            <PatientHeader patient={selectedPatient} />
            
            {/* Symptom Trend Chart */}
            <div className="mb-6">
              <SymptomTrendChart 
                data={symptomTrendData}
                onDateRangeChange={handleDateRangeChange}
              />
            </div>
            
            {/* Data Table */}
            <div className="mb-6">
              <DataTable 
                symptomLogs={mockSymptomLogs}
                cognitiveScores={mockCognitiveScores}
              />
            </div>
            
            {/* AI Analysis Panel */}
            <AIAnalysisPanel 
              patientData={selectedPatient}
              onGenerateAnalysis={handleAIAnalysisGenerated}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DetailedPatientView;